/** 人工放行 —— 当前有哪些突破风险上限的临时放行在生效、谁放的、为什么、何时到期；
 * 过去放过哪些、风控有没有真的用上；以及登记一条新的临时放行。
 *
 * 规格 `docs/design/screens/approvals.md`（2026-10-08 裁决收窄）：
 * - 只列已登记的放行记录（`GET /risk/overrides`）+ 登记表单（`POST /risk/overrides`）；
 *   「放行申请单」（申请 / 审批两步、待批队列，`/approvals`）是候选，本屏不做、不画。
 * - 生效中 / 已过期由服务端 `active` 判定，前端不比时间。
 * - scope 合法性以服务端为准（裁决 B4，其它取值 400）；停机时登记照常（裁决 B3）。
 * - **停机恢复不在本屏**：恢复下单只走停机屏的 `POST /recover`。
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { post } from "../../api/client";
import { describeError } from "../../api/errors";
import { useOverrides, useRiskLimitsRaw, useRiskReportRaw, type OverrideRecord } from "../../api/g2";
import { useHaltState, useStatus } from "../../api/queries";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { ts } from "../../lib/format";
import {
  SCOPE_INSTRUMENT_CODE,
  buildScope,
  defaultExpiry,
  localUtcToIso,
  overrideActions,
  scopeOptions,
  splitOverrides,
} from "../../lib/approvals";
import { InlineError } from "../orders/Book";

const LAYER: Record<string, string> = { instrument: "标的层", strategy: "策略层", portfolio: "组合层" };

export function ApprovalsScreen(): React.ReactNode {
  const overrides = useOverrides();
  const status = useStatus();
  const halt = useHaltState();
  const halted = status.data?.halted === true || halt.data?.halted === true;
  const groups = overrides.data ? splitOverrides(overrides.data) : null;

  return (
    <>
      <PageHead
        title="人工放行"
        sub={
          groups
            ? groups.unknown.length
              ? `历史 ${overrides.data?.length ?? 0} 条 · 是否生效：缺（接口未提供 active）`
              : `生效中 ${groups.active.length} 条 · 历史 ${overrides.data?.length ?? 0} 条`
            : "GET /risk/overrides"
        }
      />
      <Lede>
        突破风险上限的<b>临时放行</b>：谁放的、为什么、什么时候到期，风控有没有真的用上；登记时放行人、理由、有效期缺一不可。
        <b>本屏的放行不是停机恢复</b>——恢复下单只在停机屏（<code>POST /recover</code>）。
      </Lede>
      {halted ? (
        <div className="note bad" style={{ marginBottom: 12 }}>
          <b>系统停机中——临时放行不会恢复下单，恢复请去停机屏</b>　<a href="#halt">去停机屏恢复 →</a>
          {status.data?.halt_reason ? <div className="hint">停机原因（GET /status）：{status.data.halt_reason}</div> : null}
        </div>
      ) : (
        <div className="hint" style={{ marginBottom: 12 }}>
          停机恢复在<a href="#halt">停机屏</a>。
        </div>
      )}

      <div className="grid g2" style={{ marginBottom: 12 }}>
        <RecordsCard query={overrides} />
        <OverrideForm canSubmit={overrides.isSuccess} strategyIds={Object.keys(halt.data?.strategies ?? {})} />
      </div>
      <UsageCard strategyIds={Object.keys(halt.data?.strategies ?? {})} />
    </>
  );
}

function RecordsCard({ query }: { query: ReturnType<typeof useOverrides> }): React.ReactNode {
  if (query.isPending) return <Card title="放行记录"><div className="faint">取数中…</div></Card>;
  if (query.isError) {
    return (
      <Card title="放行记录" sub="GET /risk/overrides">
        <InlineError endpoint="GET /api/v1/risk/overrides" error={query.error} onRetry={() => void query.refetch()} />
        <div className="hint">放行记录读不到时风控实际读到的放行状态不明，不显示成「没有放行」。</div>
      </Card>
    );
  }
  const rows = query.data;
  if (rows.length === 0) {
    return (
      <Card title="放行记录" sub="GET /risk/overrides · 全部历史">
        <div className="empty">还没有登记过临时放行</div>
      </Card>
    );
  }
  const groups = splitOverrides(rows);
  const cols = [
    { label: "scope", mono: true },
    { label: "放行人" },
    { label: "理由", wrap: true },
    { label: "登记时间（UTC）" },
    { label: "到期（UTC）" },
    { label: "是否生效" },
  ];
  const toRow = (row: OverrideRecord, index: number) => ({
    key: `${row.granted_utc}-${index}`,
    cls: row.active === false ? "faint" : "",
    cells: [
      row.scope,
      row.operator,
      row.reason,
      row.granted_utc,
      row.expires_utc,
      row.active === null ? (
        <span className="faint">缺（接口未提供）</span>
      ) : row.active ? (
        <Pill tone="warn">生效中</Pill>
      ) : (
        <Pill tone="neutral">已过期</Pill>
      ),
    ],
  });
  if (groups.unknown.length) {
    return (
      <Card title="放行记录" sub="GET /risk/overrides · 全部历史" nopad>
        <Table cols={cols} rows={rows.map(toRow)} />
      </Card>
    );
  }
  return (
    <Card title="放行记录" sub="GET /risk/overrides · 全部历史 · active 由服务端判定" nopad>
      <div style={{ padding: "8px 12px 0" }}>
        <b>生效中</b>
      </div>
      {groups.active.length ? (
        <Table cols={cols} rows={groups.active.map(toRow)} />
      ) : (
        <div className="empty">当前没有生效的临时放行（历史 {rows.length} 条，均已过期）</div>
      )}
      <div style={{ padding: "8px 12px 0" }}>
        <b>已过期</b>
      </div>
      <Table cols={cols} rows={groups.expired.map(toRow)} empty="没有已过期的放行" />
    </Card>
  );
}

function OverrideForm({ canSubmit, strategyIds }: { canSubmit: boolean; strategyIds: string[] }): React.ReactNode {
  const limits = useRiskLimitsRaw();
  const client = useQueryClient();
  const [choice, setChoice] = useState("");
  const [code, setCode] = useState("");
  const [expires, setExpires] = useState(() => defaultExpiry(Date.now()));
  const [operator, setOperator] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const options = useMemo(() => scopeOptions(strategyIds), [strategyIds]);

  const action = useMutation({
    mutationFn: (body: Record<string, unknown>) => post<unknown>("/risk/overrides", body),
    onSuccess: () => {
      setConfirmed(false);
      void client.invalidateQueries({ queryKey: ["g2", "overrides"] });
    },
  });

  const scope = buildScope(choice, code);
  const expiresIso = localUtcToIso(expires);
  const missing: string[] = [];
  if (!scope) missing.push("scope");
  if (!expiresIso) missing.push("expires_utc");
  if (!operator.trim()) missing.push("operator");
  if (!reason.trim()) missing.push("reason");
  const blocked = !canSubmit || limits.isError;

  const submit = () => {
    action.mutate({
      scope,
      expires_utc: expiresIso,
      operator: operator.trim(),
      reason: reason.trim(),
      ...(scope.startsWith("strategy:") ? { strategy_id: scope.slice("strategy:".length) } : {}),
    });
  };

  const L = limits.data?.limits;
  return (
    <Card title="登记临时放行" sub="POST /risk/overrides · 服务端作最终判定" cls="warn">
      {limits.isError ? (
        <div className="note bad">风险配置不可用：{describeError(limits.error).why}（风控启动校验同样会失败，需先修配置）</div>
      ) : null}
      {!canSubmit ? <div className="note warn">读不到现有放行，暂不登记</div> : null}
      <label className="f">
        <span>scope（必填）</span>
        <select value={choice} onChange={(event) => setChoice(event.target.value)} disabled={blocked}>
          <option value="">（选 scope）</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {choice === SCOPE_INSTRUMENT_CODE ? (
        <label className="f">
          <span>标的代码（如 XOM.XNYS）</span>
          <input type="text" value={code} onChange={(event) => setCode(event.target.value)} disabled={blocked} />
        </label>
      ) : null}
      <label className="f">
        <span>
          expires_utc（必填，按 UTC 填；不得超过 {limits.data ? limits.data.max_ttl_hours : "?"} 小时，以服务端为准）
        </span>
        <input type="datetime-local" value={expires} onChange={(event) => setExpires(event.target.value)} disabled={blocked} />
      </label>
      <label className="f">
        <span>operator 放行人（必填）</span>
        <input type="text" value={operator} onChange={(event) => setOperator(event.target.value)} disabled={blocked} />
      </label>
      <label className="f">
        <span>reason 理由（必填）</span>
        <textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} disabled={blocked} />
      </label>
      <label className="f check">
        <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={blocked} />{" "}
        我确认：这是突破风险上限的临时许可，到期自动失效；它<b>不会</b>恢复停机
      </label>
      <button
        type="button"
        className="btn danger solid"
        disabled={blocked || missing.length > 0 || !confirmed || action.isPending}
        onClick={submit}
      >
        {action.isPending ? "提交中…" : "登记临时放行"}
      </button>
      {missing.length ? <div className="hint">还缺：{missing.join("、")}</div> : null}
      {action.isError ? <Note tone="bad">{describeError(action.error).why}</Note> : null}
      {action.isSuccess ? <Note>服务端已登记（写入 risk-overrides.json，并进事件流 kind=override）。</Note> : null}
      {L ? (
        <div className="hint" style={{ marginTop: 8 }}>
          各层当前上限（GET /risk/limits · {limits.data?.digest}）：
          {Object.entries(L)
            .map(([layer, value]) => `${LAYER[layer] ?? layer} ${JSON.stringify(value)}`)
            .join(" · ")}
          。按策略放行用 scope=strategy:&lt;sid&gt;（请求体 strategy_id 不落盘）。无撤销动作：放行只能等到期。
        </div>
      ) : null}
    </Card>
  );
}

function UsageCard({ strategyIds }: { strategyIds: string[] }): React.ReactNode {
  const [strategy, setStrategy] = useState("");
  const [date, setDate] = useState("");
  const report = useRiskReportRaw(strategy, date);
  let body: React.ReactNode;
  if (!strategy || !date) body = <div className="empty">选策略与日期查看风控是否用到了放行</div>;
  else if (report.isPending) body = <div className="faint">取数中…</div>;
  else if (report.isError) {
    const status = (report.error as { status?: number }).status;
    body =
      status === 404 ? (
        <div className="empty">该日没有风险报告（纸上盘当天未运行或风控未接入）</div>
      ) : (
        <InlineError endpoint={`GET /api/v1/risk/report?strategy=${strategy}&date=${date}`} error={report.error} onRetry={() => void report.refetch()} />
      );
  } else {
    const rows = overrideActions(report.data);
    body = rows.length ? (
      <Table
        cols={[{ label: "时间" }, { label: "层级" }, { label: "scope", mono: true }, { label: "放行人" }, { label: "理由", wrap: true }]}
        rows={rows.map((row, index) => ({
          key: String(index),
          cells: [
            typeof row["ts_event"] === "number" ? ts(row["ts_event"] as number) : String(row["ts_event"] ?? "—"),
            LAYER[String(row["layer"])] ?? String(row["layer"] ?? "—"),
            String(row["scope"] ?? "—"),
            String(row["operator"] ?? "—"),
            String(row["reason"] ?? "—"),
          ],
        }))}
      />
    ) : (
      <div className="empty">该日风控没有动用任何放行</div>
    );
  }
  return (
    <Card title="放行使用记录" sub="GET /risk/report?strategy=&date= · actions[action=override]" nopad>
      <div className="toolbar" style={{ padding: "8px 12px 0" }}>
        <select aria-label="策略" value={strategy} onChange={(event) => setStrategy(event.target.value)}>
          <option value="">（选策略）</option>
          {strategyIds.map((sid) => (
            <option key={sid} value={sid}>
              {sid}
            </option>
          ))}
        </select>
        <input type="date" aria-label="日期" value={date} onChange={(event) => setDate(event.target.value)} />
        <span className="faint">越界核对全量见<a href="#riskreport">风险核对报告</a></span>
      </div>
      {body}
    </Card>
  );
}
